/**
 * RF Variant Image V2 — runtime for the colour viewer and the separate product
 * gallery lightbox (internal replacement for the colour part of the Rubik
 * "variant images" app).
 *
 * Two independent custom elements:
 *
 *   <rf-colour-viewer>      colour viewer + swatch state (variant driven)
 *   <rf-gallery-lightbox>   large gallery viewer (never touches variants)
 *
 * The viewer is driven by the REAL Shopify variant radios that the theme renders
 * inside <variant-selects>: the visible swatch buttons explicitly click those
 * native inputs, so choosing a colour selects the real variant and the theme's own JS
 * keeps price, availability, product form, ?variant= URL and browser navigation
 * working. This file never writes the URL, never submits a form and never shows
 * price data (Ask Price / hide-price products are unaffected).
 */

(function () {
  'use strict';

  const V2 = {
    selectors: {
      viewer: '[data-rf-v2-viewer]',
      viewerImage: '[data-rf-v2-viewer-image]',
      viewerName: '[data-rf-v2-viewer-name]',
      liveRegion: '[data-rf-v2-live-region]',
      swatch: '[data-rf-v2-swatch]',
      map: '[data-rf-v2-map]',
      thumb: '[data-rf-v2-gallery-open]',
      lightboxDialog: '[data-rf-v2-lightbox-dialog]',
      lightboxImage: '[data-rf-v2-lightbox-image]',
      lightboxCaption: '[data-rf-v2-lightbox-caption]',
      lightboxPosition: '[data-rf-v2-lightbox-position]',
      lightboxClose: '[data-rf-v2-lightbox-close]',
      lightboxPrev: '[data-rf-v2-lightbox-prev]',
      lightboxNext: '[data-rf-v2-lightbox-next]',
    },
  };

  function readJson(scriptElement, fallback) {
    if (!scriptElement) return fallback;
    try {
      return JSON.parse(scriptElement.textContent) || fallback;
    } catch (error) {
      console.warn('[RF V2] could not parse config JSON', error);
      return fallback;
    }
  }

  function variantChangeEventName() {
    return typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS.variantChange : null;
  }

  /* ------------------------------------------------------------------ *
   * COLOUR VIEWER
   * ------------------------------------------------------------------ */

  if (!customElements.get('rf-colour-viewer')) {
    customElements.define(
      'rf-colour-viewer',
      class RfColourViewer extends HTMLElement {
        connectedCallback() {
          this.sectionId = this.dataset.sectionId || '';
          /* The hero viewer and the colour selectors can render in different columns, so
             every lookup falls back to a section-scoped document query. */
          this.viewerImage = this.queryV2(V2.selectors.viewerImage);
          this.viewerName = this.queryV2(V2.selectors.viewerName);
          this.liveRegion = this.queryV2(V2.selectors.liveRegion);
          this.swatches = this.queryV2(V2.selectors.swatch, true);
          this.coverButton = this.queryV2('[data-rf-v2-cover]');
          this.map = readJson(this.queryV2(V2.selectors.map), { byVariantId: {}, byValue: {} });
          /* Only the selectors' own instance wires keyboard interaction, so a swatch is
             never handled twice. */
          this.ownsSwatches = (this.dataset.rfV2Part || 'all') !== 'viewer';
          this.optionPosition = parseInt(this.dataset.optionPosition || this.map.optionPosition || 1, 10);
          this.optionName = this.map.optionName || this.dataset.optionName || '';
          this.unavailableText = this.dataset.unavailableText || '';

          this.handleChange = this.handleChange.bind(this);
          this.handleVariantChange = this.handleVariantChange.bind(this);
          this.handleSwatchKeydown = this.handleSwatchKeydown.bind(this);
          this.handleHistory = this.handleHistory.bind(this);
          this.handleCoverClick = this.handleCoverClick.bind(this);

          if (this.ownsSwatches && this.coverButton) {
            this.coverButton.addEventListener('click', this.handleCoverClick);
          }

          // The buttons proxy the theme's radios and remain keyboard reachable.
          // Unavailable colours are exposed as disabled instead and stay out of the tab
          // order, and their real radio is disabled by the Liquid template.
          this.swatches.forEach((swatch, index) => {
            const unavailable = swatch.classList.contains('is-unavailable');
            if (unavailable) {
              swatch.setAttribute('role', 'radio');
              swatch.setAttribute('aria-checked', 'false');
              swatch.setAttribute('aria-disabled', 'true');
              swatch.setAttribute('tabindex', '-1');
              swatch.dataset.rfV2SwatchIndex = String(index);
              return;
            }
            if (!swatch.hasAttribute('tabindex')) swatch.setAttribute('tabindex', '0');
            swatch.setAttribute('role', 'radio');
            swatch.setAttribute('aria-checked', swatch.classList.contains('is-selected') ? 'true' : 'false');
            swatch.dataset.rfV2SwatchIndex = String(index);
            if (this.ownsSwatches) {
              swatch.dataset.rfV2KeyBound = 'true';
              swatch.addEventListener('keydown', this.handleSwatchKeydown);
            }
          });

          /* TASK 260917T5 — paint the clicked colour on the current frame (capture phase,
             so it lands before the theme's radio/change chain and long before its fetch). */
          if (this.ownsSwatches) {
            this.addEventListener('pointerdown', this.handleSwatchPointer, true);
            this.addEventListener('click', this.handleSwatchPointer, true);
          }

          document.addEventListener('change', this.handleChange);
          window.addEventListener('pageshow', this.handleHistory);
          window.addEventListener('popstate', this.handleHistory);

          this.variantUnsubscribe = this.subscribeToVariantChange();
          this.syncFromCheckedInput();
          // V5 shows colours on demand: avoid downloading six large swatches before use.
          if (!this.closest('product-info.rf-v5-product')) this.preloadVariantImages();
        }

        /** Cross-subtree lookup: own DOM first, then the same section, then the page. */
        queryV2(selector, all = false) {
          if (all) {
            const own = [...this.querySelectorAll(selector)];
            if (own.length) return own;
            if (this.sectionId) {
              const scoped = [...document.querySelectorAll(`[data-section-id="${this.sectionId}"] ${selector}`)];
              if (scoped.length) return scoped;
            }
            return [...document.querySelectorAll(selector)];
          }
          const own = this.querySelector(selector);
          if (own) return own;
          if (this.sectionId) {
            const scoped = document.querySelector(`[data-section-id="${this.sectionId}"] ${selector}`);
            if (scoped) return scoped;
          }
          return document.querySelector(selector);
        }

        disconnectedCallback() {
          document.removeEventListener('change', this.handleChange);
          window.removeEventListener('pageshow', this.handleHistory);
          window.removeEventListener('popstate', this.handleHistory);
          this.swatches.forEach((swatch) => swatch.removeEventListener('keydown', this.handleSwatchKeydown));
          if (this.coverButton) this.coverButton.removeEventListener('click', this.handleCoverClick);
          if (typeof this.variantUnsubscribe === 'function') this.variantUnsubscribe();
        }

        subscribeToVariantChange() {
          const eventName = variantChangeEventName();
          if (!eventName) return undefined;
          return subscribe(eventName, this.handleVariantChange);
        }

        /** Colour option radios live in <variant-selects> (rendered by the theme). */
        get radios() {
          const container = this.closest('product-info') || document;
          const name = `${this.optionName}-${this.optionPosition}`;
          return Array.from(container.querySelectorAll('input[type="radio"]')).filter((input) => input.name.trim() === name);
        }

        get checkedRadio() {
          return this.radios.find((input) => input.checked) || null;
        }

        /** Ground truth: whatever the real Shopify variant radios say right now. */
        syncFromCheckedInput() {
          /* <variant-selects> updates disabled state as other options change. */
          if (this.ownsSwatches) this.syncAvailability();
          // IMPORTANT: Do NOT replace the hero image on initial sync.
          // The hero starts as product.featured_image (defaultCoverImage) and only
          // changes when the user explicitly clicks a colour swatch.
          // This prevents the default Shopify selected variant from overwriting the cover photo.
          const state = window.RFVariantImageV2State;
          const saved = state && state[this.sectionId];
          const radio = this.checkedRadio;
          if (radio) {
            // Preserve the cover on a fresh load; restore the chosen colour after AJAX re-render.
            if (saved && saved.userHasSelectedColour) {
              this.applyValue(String(radio.value), { announce: false });
            } else {
              this.setSwatchState(String(radio.value));
              if (this.viewerName) this.viewerName.textContent = radio.value;
            }
            return;
          }
          // No colour selected — show the default cover image (product.featured_image).
          if (saved && saved.userHasSelectedColour) return;
          const cover = this.map.defaultCoverImage;
          if (cover && cover.src) {
            this.applyImage(cover, null, { announce: false });
          }
        }

        /**
         * TASK 260917T5 — instant selected-state feedback.
         * The theme re-fetches and re-renders the product section on every option change,
         * so waiting for it makes a colour click feel ~700ms slow. This paints the clicked
         * state on the current frame; syncFromCheckedInput() still runs afterwards and
         * corrects it to Shopify's authoritative variant if the two ever differ.
         */
        paintSelection(value) {
          const colour = String(value);
          const entry = this.map.byValue?.[colour];
          if (!entry && !this.map.byVariantId) return;
          this.setSwatchState(colour);
          if (this.viewerName && colour) this.viewerName.textContent = colour;
          /* The map holds the same Shopify variant featured image the theme will resolve,
             so the hero image can switch now; the authoritative sync still corrects it. */
          if (entry) this.applyImage(entry, colour, { announce: false });
        }

        handleCoverClick() {
          const cover = this.map.defaultCoverImage;
          if (!cover || !cover.src) return;
          const state = (window.RFVariantImageV2State = window.RFVariantImageV2State || {});
          state[this.sectionId] = { userHasSelectedColour: false };
          this.swatches.forEach((swatch) => {
            swatch.classList.remove('is-selected');
            swatch.removeAttribute('aria-current');
            swatch.setAttribute('aria-checked', 'false');
          });
          if (this.coverButton) {
            this.coverButton.classList.add('is-selected');
            this.coverButton.setAttribute('aria-current', 'true');
          }
          this.applyImage(cover, null, { announce: false });
          if (this.viewerName) this.viewerName.textContent = 'Product';
        }

        handleSwatchPointer(event) {
          if (!this.ownsSwatches) return;
          const swatch = event.target.closest(V2.selectors.swatch);
          if (!swatch || swatch.classList.contains('is-unavailable') || swatch.getAttribute('aria-disabled') === 'true') return;
          const input = document.getElementById(swatch.dataset.rfV2SwatchInput);
          if (input && input.disabled) return;
          this.paintSelection(swatch.dataset.rfV2SwatchValue || input?.value || '');
          if (event.type === 'click') {
            const state = (window.RFVariantImageV2State = window.RFVariantImageV2State || {});
            state[this.sectionId] = { userHasSelectedColour: true };
            if (input && input.checked !== true) input.click();
          }
        }

        /**
         * TASK 260917T5 — warm the other colour images during idle time so the next click
         * swaps instantly. Bounded, de-duplicated and never awaited.
         */
        preloadVariantImages() {
          if (this.preloaded) return;
          this.preloaded = true;
          const warming = () => {
            const seen = new Set();
            let count = 0;
            for (const entry of Object.values(this.map.byValue ?? {})) {
              const src = entry?.src;
              /* TASK 260917T9: warm a sensible main-display rendition (1600px), capped lower
                 than before because each warmed file is now larger. */
              if (!src || seen.has(src) || count >= 6) continue;
              seen.add(src);
              count += 1;
              const image = new Image();
              image.decoding = 'async';
              image.src = src;
            }
          };
          if (typeof requestIdleCallback === 'function') requestIdleCallback(warming, { timeout: 2500 });
          else window.setTimeout(warming, 1200);
        }

        handleChange(event) {
          const target = event.target;
          if (!target || target.type !== 'radio') return;
          if (!this.optionNameMatches(target)) return;
          if (target.checked) {
            const st = (window.RFVariantImageV2State = window.RFVariantImageV2State || {});
            st[this.sectionId] = { userHasSelectedColour: true };
          }
          this.applyValue(target.value, { announce: true });
        }

        optionNameMatches(input) {
          if (!this.optionName) return false;
          return input.name.trim() === `${this.optionName}-${this.optionPosition}`;
        }

        handleVariantChange(event) {
          const data = event && event.data;
          if (!data) return;
          if (data.sectionId && this.sectionId && data.sectionId !== this.sectionId) return;
          const variant = data.variant;
          if (!variant) {
            this.syncFromCheckedInput();
            return;
          }

          // After theme AJAX re-render, determine the selected colour from the
          // REAL native Shopify radio — NOT from variant data or image mapping.
          // This ensures multi-option products (Colour + Thickness) always reflect
          // the actual selected colour value, regardless of which variant Shopify
          // resolved for the current option combination.
          const radio = this.checkedRadio;
          const colourValue = radio ? String(radio.value) : null;

          if (colourValue) {
            const state = window.RFVariantImageV2State;
            const saved = state && state[this.sectionId];
            if (saved && saved.userHasSelectedColour) {
              this.applyValue(colourValue, { announce: false });
            } else {
              this.setSwatchState(colourValue);
              if (this.viewerName) this.viewerName.textContent = colourValue;
            }
            return;
          }

          // Fallback: sync from DOM.
          this.syncFromCheckedInput();
        }

        handleHistory() {
          window.setTimeout(() => this.syncFromCheckedInput(), 0);
        }

        /**
         * Availability ground truth is the native Shopify radio: <variant-selects> keeps
         * its `disabled` state in sync as the shopper changes other options. Mirroring it
         * keeps the visible swatch (and its ARIA state) truthful after those changes.
         */
        setSwatchDisabled(swatch, disabled) {
          const input = document.getElementById(swatch.dataset.rfV2SwatchInput);
          swatch.classList.toggle('is-unavailable', disabled);
          swatch.disabled = disabled;
          if (disabled) {
            swatch.setAttribute('aria-disabled', 'true');
            swatch.setAttribute('aria-checked', 'false');
            swatch.setAttribute('tabindex', '-1');
            if (this.unavailableText) {
              swatch.setAttribute('title', this.unavailableText);
              swatch.setAttribute('aria-label', this.unavailableText);
            }
            return;
          }
          swatch.removeAttribute('aria-disabled');
          if (this.unavailableText) {
            swatch.removeAttribute('title');
            swatch.removeAttribute('aria-label');
          }
          if (!swatch.hasAttribute('tabindex') || swatch.getAttribute('tabindex') === '-1') {
            swatch.setAttribute('tabindex', '0');
          }
          if (!swatch.dataset.rfV2KeyBound) {
            swatch.addEventListener('keydown', this.handleSwatchKeydown);
            swatch.dataset.rfV2KeyBound = 'true';
          }
          swatch.setAttribute('aria-checked', swatch.classList.contains('is-selected') ? 'true' : 'false');
        }

        syncAvailability() {
          this.swatches.forEach((swatch) => {
            const input = document.getElementById(swatch.dataset.rfV2SwatchInput);
            if (!input) return;
            this.setSwatchDisabled(swatch, input.disabled === true);
          });
        }

        /** Arrow-key support supplements the native keyboard behaviour of each button. */
        handleSwatchKeydown(event) {
          const swatch = event.currentTarget;
          if (!swatch || swatch.classList.contains('is-unavailable') || swatch.getAttribute('aria-disabled') === 'true') return;
          const index = Number(swatch.dataset.rfV2SwatchIndex || 0);
          const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
          if (!keys.includes(event.key)) return;
          event.preventDefault();

          /* Arrow/Home/End movement skips unavailable colours: they are not selectable. */
          const selectable = this.swatches.filter((node) => !node.classList.contains('is-unavailable'));
          if (!selectable.length) return;

          let target = swatch;
          if (event.key === 'Home') {
            target = selectable[0];
          } else if (event.key === 'End') {
            target = selectable[selectable.length - 1];
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            const position = selectable.indexOf(swatch);
            target = selectable[(position + 1) % selectable.length];
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            const position = selectable.indexOf(swatch);
            target = selectable[(position - 1 + selectable.length) % selectable.length];
          }
          if (!target || target.classList.contains('is-unavailable')) return;
          target.focus();
          target.click();
        }

        /** Applies a colour value: image + name + selected swatch state. */
        applyValue(value, options) {
          const entry = (this.map.byValue || {})[String(value)];
          if (entry) {
            this.applyImage(entry, String(value), options);
            return;
          }
          this.setSwatchState(String(value));
          if (options && options.announce) this.announce(String(value));
        }

        applyImage(entry, colour, options) {
          if (this.viewerImage && entry.src) {
            this.viewerImage.src = entry.src;
            if (entry.srcset) {
              this.viewerImage.srcset = entry.srcset;
            } else {
              this.viewerImage.removeAttribute('srcset');
            }
            if (entry.width && entry.height) {
              this.viewerImage.width = entry.width;
              this.viewerImage.height = entry.height;
            }
            if (entry.alt !== undefined) this.viewerImage.alt = entry.alt;
          }
          if (this.viewerName && colour) this.viewerName.textContent = colour;
          this.setSwatchState(colour);
          if (options && options.announce) this.announce(colour);
        }

        setSwatchState(colour) {
          this.swatches.forEach((swatch) => {
            const isSelected = swatch.dataset.rfV2SwatchValue === colour;
            swatch.classList.toggle('is-selected', isSelected);
            swatch.setAttribute('aria-checked', isSelected ? 'true' : 'false');
            if (isSelected) {
              swatch.setAttribute('aria-current', 'true');
            } else {
              swatch.removeAttribute('aria-current');
            }
          });
        }

        announce(colour) {
          if (!this.liveRegion || !colour) return;
          const label = this.optionName || 'Colour';
          this.liveRegion.textContent = `${label}: ${colour}`;
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * PRODUCT GALLERY LIGHTBOX (independent of the colour viewer)
   * ------------------------------------------------------------------ */

  if (!customElements.get('rf-gallery-lightbox')) {
    customElements.define(
      'rf-gallery-lightbox',
      class RfGalleryLightbox extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector(V2.selectors.lightboxDialog);
          this.image = this.querySelector(V2.selectors.lightboxImage);
          this.caption = this.querySelector(V2.selectors.lightboxCaption);
          this.positionLabel = this.querySelector(V2.selectors.lightboxPosition);
          // The gallery root holds the thumbnails; the modal half of the same snippet is
          // rendered outside the sticky product-information column (so position: fixed
          // anchors to the viewport), so fall back to the page-level gallery root.
          this.host = this.hasAttribute('data-rf-smp-parent-lightbox')
            ? this.closest('product-info')?.querySelector('[data-rf-smp-parent-gallery]')
            : (this.hasAttribute('data-rf-colour-lightbox')
              ? document.querySelector('[data-rf-native-preview]')
              : (this.closest('rf-product-gallery-v2') || document.querySelector('rf-product-gallery-v2[data-rf-v2-gallery]')));
          this.thumbs = this.host ? Array.from(this.host.querySelectorAll(this.hasAttribute('data-rf-colour-lightbox') ? '[data-rf-native-colour]:not([disabled])' : V2.selectors.thumb)) : [];
          this.index = 0;
          this.opener = null;

          this.handleKeydown = this.handleKeydown.bind(this);
          this.handleTouchStart = this.handleTouchStart.bind(this);
          this.handleTouchEnd = this.handleTouchEnd.bind(this);
          this.handleThumbClick = this.handleThumbClick.bind(this);

          if (this.host) this.host.addEventListener('click', this.handleThumbClick);
          this.querySelectorAll(V2.selectors.lightboxClose).forEach((element) =>
            element.addEventListener('click', (event) => {
              event.preventDefault();
              this.close();
            })
          );
          const prev = this.querySelector(V2.selectors.lightboxPrev);
          const next = this.querySelector(V2.selectors.lightboxNext);
          if (prev) prev.addEventListener('click', () => this.previous());
          if (next) next.addEventListener('click', () => this.next());

          if (this.dialog) {
            this.dialog.addEventListener('touchstart', this.handleTouchStart, { passive: true });
            this.dialog.addEventListener('touchend', this.handleTouchEnd, { passive: true });
          }
        }

        disconnectedCallback() {
          if (this.host) this.host.removeEventListener('click', this.handleThumbClick);
          this.restoreFloatingWidgets();
          this.teardown();
        }

        get isOpen() {
          return !this.hasAttribute('hidden');
        }

        handleThumbClick(event) {
          const thumb = event.target.closest(V2.selectors.thumb);
          if (!thumb) return;
          event.preventDefault();
          const index = this.thumbs.indexOf(thumb);
          this.open(index === -1 ? 0 : index, thumb);
        }

        open(index, opener) {
          if (!this.thumbs.length) return;
          this.opener = opener || null;
          this.show(index);
          this.removeAttribute('hidden');
          this.lockScroll();
          this.suppressFloatingWidgets();
          document.addEventListener('keydown', this.handleKeydown, true);
          const closeButton = this.querySelector(V2.selectors.lightboxClose);
          const focusTarget = closeButton || this.dialog;
          if (focusTarget && typeof focusTarget.focus === 'function') focusTarget.focus();
        }

        close() {
          if (!this.isOpen) return;
          this.setAttribute('hidden', '');
          this.unlockScroll();
          this.restoreFloatingWidgets();
          document.removeEventListener('keydown', this.handleKeydown, true);
          // Returning focus to the thumbnail keeps the customer in exactly the same
          // place: same product, same selected colour, same selected variant.
          if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
        }

        /**
         * TASK 260917T4-FIX — chat/review apps inject fixed iframes with very high
         * z-indexes that cover the bottom-left corner of the screen, which is exactly
         * where the ALT caption sits. While the viewer is open those floating layers are
         * parked (remembered and fully restored on close); nothing is removed, and
         * anything inside this overlay (or in normal flow) is left alone.
         */
        suppressFloatingWidgets() {
          if (this.parkedLayers) return;
          this.parkedLayers = [];
          const park = (node) => {
            if (!node || node === document.body || node === document.documentElement) return;
            if (this.contains(node)) return;
            if (this.parkedLayers.some((entry) => entry.node === node)) return;
            this.parkedLayers.push({ node, visibility: node.style.visibility });
            node.style.visibility = 'hidden';
          };
          /* 1. exactly what covers the overlay: probe the caption area and the stage, then
                climb to the outermost fixed/sticky ancestor (chat widgets wrap their
                iframe in a fixed container that the iframe itself does not carry). */
          const fractions = [
            [0.18, 0.96], [0.5, 0.96], [0.85, 0.96],
            [0.18, 0.75], [0.5, 0.5], [0.18, 0.25],
          ];
          for (const [fx, fy] of fractions) {
            const hit = document.elementFromPoint(window.innerWidth * fx, window.innerHeight * fy);
            if (!hit || this.contains(hit)) continue;
            let outermost = null;
            for (let el = hit; el && el !== document.body; el = el.parentElement) {
              const position = getComputedStyle(el).position;
              if (position === 'fixed' || position === 'sticky') outermost = el;
            }
            park(outermost);
          }
          /* 2. belt and braces: fixed/sticky chat and review layers by name. */
          for (const node of document.querySelectorAll('iframe, [id*="tawk" i], [class*="tawk" i], [id*="chat" i], [class*="chat-widget" i], [class*="gorgias" i]')) {
            if (this.contains(node)) continue;
            const style = getComputedStyle(node);
            if (style.position !== 'fixed' && style.position !== 'sticky') continue;
            if (style.display === 'none' || style.visibility === 'hidden') continue;
            park(node);
          }
        }

        restoreFloatingWidgets() {
          if (!this.parkedLayers) return;
          for (const entry of this.parkedLayers) {
            entry.node.style.visibility = entry.visibility;
          }
          this.parkedLayers = null;
        }

        next() {
          this.show(this.index + 1);
        }

        previous() {
          this.show(this.index - 1);
        }

        show(index) {
          const total = this.thumbs.length;
          if (!total) return;
          this.index = ((index % total) + total) % total;
          const thumb = this.thumbs[this.index];
          if (!thumb) return;

          const src = thumb.dataset.rfV2FullSrc || '';
          const srcset = thumb.dataset.rfV2FullSrcset || '';
          const alt = thumb.dataset.rfV2Alt || '';
          /* TASK 260917T3: the fullscreen caption is the Inspiration image's ALT text,
             nothing else. Blank ALT hides the caption completely. */
          const caption = alt;

          if (this.image) {
            this.image.src = src;
            if (srcset) {
              this.image.srcset = srcset;
            } else {
              this.image.removeAttribute('srcset');
            }
            if (thumb.dataset.rfV2FullWidth) this.image.width = thumb.dataset.rfV2FullWidth;
            if (thumb.dataset.rfV2FullHeight) this.image.height = thumb.dataset.rfV2FullHeight;
            // The Shopify image's own alt text is always preserved on the <img>;
            // visible captions are handled separately by the gallery_captions setting.
            this.image.alt = alt;
          }
          if (this.caption) {
            if (caption) {
              this.caption.textContent = caption;
              this.caption.removeAttribute('hidden');
            } else {
              this.caption.textContent = '';
              this.caption.setAttribute('hidden', '');
            }
          }
          if (this.positionLabel) this.positionLabel.textContent = String(this.index + 1);
        }

        handleKeydown(event) {
          if (!this.isOpen) return;
          switch (event.key) {
            case 'Escape':
              event.preventDefault();
              this.close();
              break;
            case 'ArrowRight':
              event.preventDefault();
              this.next();
              break;
            case 'ArrowLeft':
              event.preventDefault();
              this.previous();
              break;
            case 'Home':
              event.preventDefault();
              this.show(0);
              break;
            case 'End':
              event.preventDefault();
              this.show(this.thumbs.length - 1);
              break;
            case 'Tab':
              this.trapFocus(event);
              break;
            default:
              break;
          }
        }

        trapFocus(event) {
          if (!this.dialog) return;
          const focusable = Array.from(
            this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
          ).filter((element) => !element.hasAttribute('disabled'));
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }

        handleTouchStart(event) {
          const touch = event.changedTouches[0];
          if (!touch) return;
          this.touchStartX = touch.clientX;
          this.touchStartY = touch.clientY;
        }

        handleTouchEnd(event) {
          const touch = event.changedTouches[0];
          if (!touch || this.touchStartX === undefined) return;
          const deltaX = touch.clientX - this.touchStartX;
          const deltaY = touch.clientY - this.touchStartY;
          this.touchStartX = undefined;
          this.touchStartY = undefined;
          if (Math.abs(deltaX) < 40 || Math.abs(deltaX) < Math.abs(deltaY)) return;
          if (deltaX < 0) {
            this.next();
          } else {
            this.previous();
          }
        }

        lockScroll() {
          const scrollbar = window.innerWidth - document.documentElement.clientWidth;
          document.body.style.overflow = 'hidden';
          if (scrollbar > 0) document.body.style.paddingRight = `${scrollbar}px`;
        }

        unlockScroll() {
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
        }

        teardown() {
          document.removeEventListener('keydown', this.handleKeydown, true);
          if (this.isOpen) this.unlockScroll();
        }
      }
    );
  }
})();
